import React, { useState } from 'react';
import { CreateLocationDTO } from '@peartherapeutics/weaver-api-client';
import { withStyles } from '@material-ui/styles';
import { Dialog, DialogContent } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import ButtonMedium from 'components/common/Buttons/ButtonMedium';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { designConstants as design } from 'styles/designConstants';
import AddLocationForm from './AddLocationForm';
import ConfirmAddLocationForm from './ConfirmLocationForm';
import './styles.scss';

export const addLocationSuccessMessage = 'New location was added successfully';

export interface ILocationFormData {
  location: CreateLocationDTO;
}

interface IProps {
  getAllLocations: (action?: string) => Promise<void>;
  classes: any;
}

const customStyle = () => ({
  dialog: {
    '& .MuiDialog-paper': {
      color: design.color.$black_full,
      height: 'auto',
      padding: '20px',
      width: '400px',
    },
  },
  content: { padding: '0px' },
  closeButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    gridColumn: 2,
  },
  formHeader: {
    padding: '0',
  },
  formTitle: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '30px',
    gridColumn: 1,
  },
});

const AddLocationButton = (props: IProps) => {
  const [isOpenAddLocationModal, setIsOpenAddLocationModal] =
    useState<boolean>(false);
  const [isOpenConfirmAddLocationModal, setIsOpenConfirmAddLocationModal] =
    useState<boolean>(false);
  const [clearFormData, setClearFormData] = useState<boolean>(false);
  const [data, setData] = useState<Nullable<ILocationFormData>>(null);

  const closeAddLocationModal = () => {
    setClearFormData(true);
    setIsOpenAddLocationModal(false);
  };

  const openAddLocationModal = () => {
    setClearFormData(false);
    setIsOpenAddLocationModal(true);
  };

  const openConfirmAddLocationModal = () => {
    setIsOpenAddLocationModal(false);
    setIsOpenConfirmAddLocationModal(true);
  };

  const submitAddLocationForm = (data: ILocationFormData) => {
    setData(data);
    closeAddLocationModal();
    openConfirmAddLocationModal();
  };

  const closeConfirmAddLocationModal = () => {
    setIsOpenConfirmAddLocationModal(false);
    setData(null);
  };

  const submitConfirmAddLocationForm = async () => {
    if (!data) return;
    try {
      const { location } = data;
      await WeaverApiClientInstance.createLocation(location);
      await props.getAllLocations('first-page');
      closeConfirmAddLocationModal();
      ErrorInterceptor.success(addLocationSuccessMessage, true);
    } catch (e) {
      let errors = e;
      if (e.error) {
        errors = e.errors.map((m: any) => ({ message: m.message }));
      }

      if (e.response) {
        errors = { message: e.response.data.message };
      }

      ErrorInterceptor.error({ errors });
    }
  };

  const { classes } = props;
  return (
    <>
      <ButtonMedium
        buttonRole={'primary'}
        buttonType="submit"
        width={'240px'}
        handleClick={openAddLocationModal}
      >
        ADD LOCATION
      </ButtonMedium>
      <Dialog
        className={classes.dialog}
        data-testid="add-location-form-modal"
        open={isOpenAddLocationModal}
        keepMounted
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        onClose={closeAddLocationModal}
      >
        <div className={classes.formHeader} styleName="form-header">
          <div className={classes.formTitle}>Add Location</div>
          <span className={classes.closeButton}>
            <IconButton
              data-testid="button-modal-close-x"
              aria-label="close"
              edge="end"
              onClick={closeAddLocationModal}
            >
              <CloseIcon />
            </IconButton>
          </span>
        </div>

        <DialogContent className={classes.content}>
          <AddLocationForm
            clearFormData={clearFormData}
            handleSubmit={submitAddLocationForm}
          />
        </DialogContent>
      </Dialog>

      {data && (
        <Dialog
          className={classes.dialog}
          data-testid="add-location-confirm-modal"
          open={isOpenConfirmAddLocationModal}
          keepMounted
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          onClose={closeConfirmAddLocationModal}
        >
          <div className={classes.formHeader} styleName="form-header">
            <div className={classes.formTitle}>Confirm Location</div>
            <span className={classes.closeButton}>
              <IconButton
                data-testid="button-modal-close-x"
                aria-label="close"
                edge="end"
                onClick={closeConfirmAddLocationModal}
              >
                <CloseIcon />
              </IconButton>
            </span>
          </div>

          <DialogContent className={classes.content}>
            <ConfirmAddLocationForm
              data={data}
              handleSubmit={submitConfirmAddLocationForm}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default withStyles(customStyle)(AddLocationButton);
